import { useEffect, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { CalendarClock, CalendarOff, Clock, ImagePlus, MoonStar, Save, Snowflake, Sun, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/ui/date-input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const WORKDAYS = [["6", "الأحد"], ["0", "الاثنين"], ["1", "الثلاثاء"], ["2", "الأربعاء"], ["3", "الخميس"]];

const LogoPicker = ({ label, value, onChange, testId }) => (  <div className="space-y-1.5">
    <Label>{label}</Label>
    <div className="flex items-center gap-3">
      <label className="cursor-pointer flex items-center gap-2 border border-dashed border-slate-300 rounded-lg px-3 py-2 text-xs text-slate-500 hover:border-emerald-600">
        <ImagePlus className="w-4 h-4" />
        {value ? "تغيير الصورة" : "اختيار صورة"}
        <input type="file" accept="image/*" className="hidden" data-testid={testId}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            if (f.size > 400 * 1024) { toast.error("حجم الصورة يجب أن يكون أقل من 400KB"); return; }
            const reader = new FileReader();
            reader.onload = () => onChange(reader.result);
            reader.readAsDataURL(f);
          }} />
      </label>
      {value && (
        <>
          <img src={value} alt={label} className="w-12 h-12 object-contain border rounded-lg bg-white" />
          <button type="button" onClick={() => onChange(null)} className="text-xs text-rose-600 hover:underline">إزالة</button>
        </>
      )}
    </div>
  </div>
);

export default function SettingsTab({ section = "all" }) {
  const [s, setS] = useState(null);
  const [b, setB] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [hName, setHName] = useState("");
  const [hFrom, setHFrom] = useState("");
  const [hTo, setHTo] = useState("");
  const [hSaving, setHSaving] = useState(false);
  const [daysOpen, setDaysOpen] = useState({});

  const loadHolidays = () => api.get("/holidays").then((r) => setHolidays(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => {});
    api.get("/branding").then((r) => setB(r.data)).catch(() => {});
    loadHolidays();
  }, []);

  const saveSettings = async () => {
    try {
      const { data } = await api.put("/settings", s);
      setS(data);
      toast.success("تم حفظ أوقات الدوام — ستُحسب التقارير وفقها فوراً");
    } catch { toast.error("فشل الحفظ"); }
  };

  const saveBranding = async () => {
    try {
      // stamp option retired from settings — always clear it on save
      const { data } = await api.put("/branding", { ...b, stamp: null });
      setB(data);
      toast.success("تم حفظ الترويسة والشعارات");
    } catch (e) { toast.error(e.response?.data?.detail || "فشل الحفظ"); }
  };

  const addHoliday = async () => {
    if (!hName.trim()) { toast.error("أدخل اسم الإجازة"); return; }
    if (!hFrom || !hTo || hFrom > hTo) { toast.error("تأكد من تاريخي البداية والنهاية"); return; }
    setHSaving(true);
    try {
      const { data: h } = await api.post("/holidays", { name: hName.trim(), from_date: hFrom, to_date: hTo });
      toast.success(h.purged_records
        ? `تمت إضافة الإجازة — حُذفت تلقائياً ${h.purged_records} سجل دوام و${h.purged_inquiries} مساءلة مرتبطة بأيامها`
        : "تمت إضافة الإجازة — أيامها مستثناة تلقائياً من الاستيراد والغياب والتأخر والمساءلات");
      setHName(""); setHFrom(""); setHTo("");
      loadHolidays();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل إضافة الإجازة"); }
    finally { setHSaving(false); }
  };

  const deleteHoliday = async (h) => {
    if (!window.confirm(`حذف إجازة "${h.name}"؟ ستُحتسب أيامها في الغياب والتأخر من جديد`)) return;
    try {
      await api.delete(`/holidays/${h.id}`);
      toast.success("تم حذف الإجازة");
      loadHolidays();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل حذف الإجازة"); }
  };

  if (!s || !b) return <div className="py-20 text-center text-slate-400" data-testid="settings-loading">جارٍ التحميل...</div>;

  const num = (k) => ({
    value: s[k],
    onChange: (e) => setS({ ...s, [k]: parseInt(e.target.value || "0", 10) }),
  });

  const setSchedTime = (sched, part, val) => {
    const next = { ...s, [`${sched}_work_${part}`]: val };
    if (s.active_schedule === sched) next[`work_${part}`] = val;
    setS(next);
  };

  const activateSched = (sched) => {
    setS({ ...s, active_schedule: sched,
           work_start: s[`${sched}_work_start`], work_end: s[`${sched}_work_end`] });
  };

  const setDayTime = (sched, wd, part, val) => {
    const dt = { ...(s[`${sched}_day_times`] || {}) };
    const cur = { ...(dt[wd] || {}) };
    cur[part] = val || null;
    if (!cur.start && !cur.end) delete dt[wd]; else dt[wd] = cur;
    setS({ ...s, [`${sched}_day_times`]: dt });
  };

  const renderDayTimes = (sched) => (
    <div className="space-y-1">
      <button type="button" data-testid={`btn-days-toggle-${sched}`}
        onClick={() => setDaysOpen((p) => ({ ...p, [sched]: !p[sched] }))}
        className="text-[10px] font-bold text-slate-500 hover:text-emerald-800 transition-colors">
        {daysOpen[sched] ? "إخفاء تخصيص الأيام ▴" : "تخصيص وقت كل يوم ▾"}
      </button>
      {daysOpen[sched] && (
        <div className="space-y-1 border-t border-dashed border-slate-200 pt-1.5">
          {WORKDAYS.map(([wd, name]) => (
            <div key={wd} className="grid grid-cols-[52px_1fr_1fr] items-center gap-1.5"
              data-testid={`day-row-${sched}-${wd}`}>
              <span className="text-[10px] font-bold text-slate-500">{name}</span>
              <Input type="time" dir="ltr" className="h-7 text-[11px]"
                value={((s[`${sched}_day_times`] || {})[wd] || {}).start || ""}
                data-testid={`day-${sched}-${wd}-start`}
                onChange={(e) => setDayTime(sched, wd, "start", e.target.value)} />
              <Input type="time" dir="ltr" className="h-7 text-[11px]"
                value={((s[`${sched}_day_times`] || {})[wd] || {}).end || ""}
                data-testid={`day-${sched}-${wd}-end`}
                onChange={(e) => setDayTime(sched, wd, "end", e.target.value)} />
            </div>
          ))}
          <p className="text-[9px] text-slate-400">الحقل الفارغ يتبع وقت البطاقة العام</p>
        </div>
      )}
    </div>
  );

  const saveSettingsBtn = (
    <Button onClick={saveSettings} data-testid="btn-save-settings"
      className="bg-emerald-900 hover:bg-emerald-950 text-white gap-1.5">
      <Save className="w-4 h-4" /> حفظ الإعدادات
    </Button>
  );

  const timingCard = (
    <div className="bg-white rounded-2xl border shadow-sm p-6 space-y-5" data-testid="settings-section-timing">
      <div className="flex items-center gap-2">
        <Clock className="w-5 h-5 text-emerald-800" />
        <h3 className="font-bold">توقيت الدوام الرسمي</h3>
      </div>
      <p className="text-xs text-slate-400 leading-6">
        فعّل التوقيت الصيفي أو الشتوي أو توقيت شهر رمضان المبارك — تُحتسب السجلات الجديدة وفق التوقيت
        المفعّل، وتبقى السجلات السابقة محسوبة بالتوقيت الذي كان مفعّلاً وقت تسجيلها ولا تتغير عند التبديل.
        فترة السماح مشتركة لكل التوقيتات.
      </p>
      <div className="grid grid-cols-2 gap-3">
        {[
          { key: "summer", label: "التوقيت الصيفي", Icon: Sun },
          { key: "winter", label: "التوقيت الشتوي", Icon: Snowflake },
          { key: "ramadan", label: "توقيت شهر رمضان المبارك", Icon: MoonStar },
        ].map(({ key, label, Icon }) => (
          <div key={key} data-testid={`schedule-card-${key}`}
            className={`rounded-xl border p-3 space-y-2 transition-colors ${
              s.active_schedule === key ? "border-emerald-700 bg-emerald-50/60" : "border-slate-200"}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold flex items-center gap-1.5">
                <Icon className="w-4 h-4 text-amber-500" /> {label}
              </span>
              {s.active_schedule === key ? (
                <span className="text-[10px] font-bold bg-emerald-900 text-white rounded-full px-2 py-0.5"
                  data-testid={`badge-active-${key}`}>مفعّل</span>
              ) : (
                <button type="button" onClick={() => activateSched(key)} data-testid={`btn-activate-${key}`}
                  className="text-[10px] font-bold text-emerald-800 border border-emerald-700 rounded-full px-2 py-0.5 hover:bg-emerald-50">
                  تفعيل
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-[11px]">بداية الدوام</Label>
                <Input type="time" value={s[`${key}_work_start`] || ""} dir="ltr" className="h-8 text-xs"
                  data-testid={`setting-${key}-start`}
                  onChange={(e) => setSchedTime(key, "start", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px]">نهاية الدوام</Label>
                <Input type="time" value={s[`${key}_work_end`] || ""} dir="ltr" className="h-8 text-xs"
                  data-testid={`setting-${key}-end`}
                  onChange={(e) => setSchedTime(key, "end", e.target.value)} />
              </div>
            </div>
            {renderDayTimes(key)}
          </div>
        ))}
        <div data-testid="schedule-card-flexible"
          className={`rounded-xl border p-3 space-y-2 transition-colors ${
            s.active_schedule === "flexible" ? "border-emerald-700 bg-emerald-50/60" : "border-slate-200"}`}>
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold flex items-center gap-1.5">
              <CalendarClock className="w-4 h-4 text-emerald-700" /> التوقيت المرن
            </span>
            {s.active_schedule === "flexible" ? (
              <span className="text-[10px] font-bold bg-emerald-900 text-white rounded-full px-2 py-0.5"
                data-testid="badge-active-flexible">مفعّل</span>
            ) : (
              <button type="button" onClick={() => activateSched("flexible")} data-testid="btn-activate-flexible"
                className="text-[10px] font-bold text-emerald-800 border border-emerald-700 rounded-full px-2 py-0.5 hover:bg-emerald-50">
                تفعيل
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-[11px]">بداية الدوام</Label>
                <button type="button" data-testid="check-flexible-start-open"
                  onClick={() => {
                    const v = s.flexible_work_start === "open" ? "08:00" : "open";
                    setS(s.active_schedule === "flexible"
                      ? { ...s, flexible_work_start: v, work_start: v }
                      : { ...s, flexible_work_start: v });
                  }}
                  className={`text-[10px] font-bold rounded-full px-2 py-0.5 border transition-colors ${
                    s.flexible_work_start === "open"
                      ? "bg-sky-600 text-white border-sky-600"
                      : "text-sky-800 border-sky-300 hover:bg-sky-50"}`}>
                  مفتوح
                </button>
              </div>
              <Input type="time" dir="ltr" className="h-8 text-xs"
                disabled={s.flexible_work_start === "open"}
                value={s.flexible_work_start === "open" ? "" : (s.flexible_work_start || "08:00")}
                data-testid="setting-flexible-start"
                onChange={(e) => setS(s.active_schedule === "flexible"
                  ? { ...s, flexible_work_start: e.target.value, work_start: e.target.value }
                  : { ...s, flexible_work_start: e.target.value })} />
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-[11px]">نهاية الدوام</Label>
                <button type="button" data-testid="check-flexible-end-open"
                  onClick={() => {
                    const v = s.flexible_work_end === "open" ? "12:00" : "open";
                    setS(s.active_schedule === "flexible"
                      ? { ...s, flexible_work_end: v, work_end: v }
                      : { ...s, flexible_work_end: v });
                  }}
                  className={`text-[10px] font-bold rounded-full px-2 py-0.5 border transition-colors ${
                    s.flexible_work_end === "open"
                      ? "bg-sky-600 text-white border-sky-600"
                      : "text-sky-800 border-sky-300 hover:bg-sky-50"}`}>
                  مفتوح
                </button>
              </div>
              <Input type="time" dir="ltr" className="h-8 text-xs"
                disabled={s.flexible_work_end === "open"}
                value={s.flexible_work_end === "open" ? "" : (s.flexible_work_end || "12:00")}
                data-testid="setting-flexible-end"
                onChange={(e) => setS(s.active_schedule === "flexible"
                  ? { ...s, flexible_work_end: e.target.value, work_end: e.target.value }
                  : { ...s, flexible_work_end: e.target.value })} />
            </div>
          </div>
          {renderDayTimes("flexible")}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1.5">
          <Label className="text-[11px]">فترة السماح لبداية الدوام (دقائق)</Label>
          <Input type="number" min="0" dir="ltr" data-testid="setting-grace" {...num("grace_minutes")} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[11px]">فترة السماح لنهاية الدوام (دقائق)</Label>
          <Input type="number" min="0" dir="ltr" data-testid="setting-early-grace"
            value={s.early_grace_minutes ?? s.grace_minutes ?? 15}
            onChange={(e) => setS({ ...s, early_grace_minutes: parseInt(e.target.value || "0", 10) })} />
        </div>
      </div>
      {saveSettingsBtn}
    </div>
  );

  const inquiriesCard = (
    <div className="bg-white rounded-2xl border shadow-sm p-6 space-y-5" data-testid="settings-section-inquiries">
      <div className="space-y-1">
        <Label className="font-bold">عتبات المساءلات التلقائية</Label>
        <p className="text-[11px] text-slate-400 leading-5">
          كلما تجاوز إجمالي دقائق الموظف العتبة أو أحد مضاعفاتها صدرت مساءلة تلقائياً بالإجمالي
          التراكمي، وتُصدر مساءلات الغياب والخروج بدون بصمة تلقائياً بعد كل استيراد وعبر فحص يومي.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>عتبة دقائق التأخر</Label>
          <Input type="number" min="1" dir="ltr" data-testid="setting-late-threshold-minutes" {...num("late_threshold_minutes")} />
        </div>
        <div className="space-y-1.5">
          <Label>عتبة دقائق الانصراف المبكر</Label>
          <Input type="number" min="1" dir="ltr" data-testid="setting-early-threshold-minutes" {...num("early_threshold_minutes")} />
        </div>
      </div>
      <div className="border-t pt-4 flex items-center justify-between gap-4">
        <div className="space-y-1">
          <Label>تسجيل الغياب تلقائياً عند الاستيراد</Label>
          <p className="text-[11px] text-slate-400 leading-5">
            عند استيراد سجل الدوام، يُسجَّل تلقائياً أي موظف غير موجود في الملف كـ "غائب" في ذلك اليوم.
          </p>
        </div>
        <Switch checked={!!s.auto_absent} onCheckedChange={(v) => setS({ ...s, auto_absent: v })}
          data-testid="setting-auto-absent" dir="ltr" />
      </div>
      <div className="border-t pt-4 space-y-3">
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-1">
            <Label>احتساب دقائق الانصراف بلا بصمة</Label>
            <p className="text-[11px] text-slate-400 leading-5">
              عند التفعيل يُحتسب الموظف الذي سجّل حضوراً بدون بصمة انصراف كأنه انصرف مبكراً
              بالدقائق المحددة أدناه، وعند الإيقاف يُتجاهل تماماً ولا يؤثر على التقارير والمساءلات.
            </p>
          </div>
          <Switch checked={!!s.missing_checkout_early}
            onCheckedChange={(v) => setS({ ...s, missing_checkout_early: v })}
            data-testid="setting-missing-checkout-early" dir="ltr" />
        </div>
        {!!s.missing_checkout_early && (
          <div className="space-y-1.5 max-w-[220px]">
            <Label>عدد الدقائق المحتسبة لكل يوم بلا بصمة انصراف</Label>
            <Input type="number" min="1" max="720" dir="ltr"
              data-testid="setting-missing-checkout-minutes" {...num("missing_checkout_minutes")} />
          </div>
        )}
      </div>
      <div className="border-t pt-4 flex items-center justify-between gap-4">
        <div className="space-y-1">
          <Label>التصحيح التلقائي لأخطاء الاستيراد</Label>
          <p className="text-[11px] text-slate-400 leading-5">
            عند التفعيل تُصحَّح أخطاء التبصيم الشائعة تلقائياً عند الاستيراد (بصمة انصراف بدون حضور
            تُحتسب حضوراً، وبصمة الانصراف غير الصالحة تُتجاهل) مع شارة تنبيه في سجل الدوام،
            وعند الإيقاف تُستورد البيانات كما هي دون أي تصحيح.
          </p>
        </div>
        <Switch checked={s.auto_fix_import !== false}
          onCheckedChange={(v) => setS({ ...s, auto_fix_import: v })}
          data-testid="setting-auto-fix-import" dir="ltr" />
      </div>
      <div className="border-t pt-4 space-y-1.5">
        <Label>تاريخ بداية الاستيراد من حضوري</Label>
        <p className="text-[11px] text-slate-400 leading-5">
          يبدأ الاستيراد التلقائي (زر «استيراد من حضوري» في سجل الدوام) من هذا التاريخ ويستكمل
          الأيام الناقصة فقط دون تكرار الموجود. غيّره متى شئت — يسري اعتباراً من الاستيراد التالي.
        </p>
        <DateInput value={s.import_start_date || ""} data-testid="setting-import-start-date"
          onChange={(e) => setS({ ...s, import_start_date: e.target.value || null })}
          className="h-9 max-w-[220px]" />
      </div>
      {saveSettingsBtn}
    </div>
  );

  const holidaysCard = (
    <div className="bg-white rounded-2xl border shadow-sm p-6 space-y-5" data-testid="holidays-section">
      <div className="space-y-1">
        <Label className="font-bold flex items-center gap-1.5">
          <CalendarOff className="w-4 h-4 text-emerald-700" /> الإجازات والعطل
        </Label>
        <p className="text-[11px] text-slate-400 leading-5">
          أي يوم يقع ضمن فترة إجازة مضافة يُتجاهل تلقائياً عند حساب الغياب والتأخر والخروج المبكر
          ولا تُنشأ عنه مساءلة.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1 col-span-2">
          <Label className="text-[11px]">اسم الإجازة</Label>
          <Input value={hName} onChange={(e) => setHName(e.target.value)}
            data-testid="holiday-name" placeholder="مثال: اليوم الوطني" className="h-9 text-xs" />
        </div>
        <div className="space-y-1">
          <Label className="text-[11px]">من تاريخ</Label>
          <DateInput type="date" value={hFrom} onChange={(e) => setHFrom(e.target.value)}
            data-testid="holiday-from" className="h-9 text-xs" />
        </div>
        <div className="space-y-1">
          <Label className="text-[11px]">إلى تاريخ</Label>
          <DateInput type="date" value={hTo} onChange={(e) => setHTo(e.target.value)}
            data-testid="holiday-to" className="h-9 text-xs" />
        </div>
      </div>
      <div>
        <Button size="sm" onClick={addHoliday} disabled={hSaving} data-testid="btn-add-holiday"
          className="bg-emerald-900 hover:bg-emerald-950 text-white h-9 text-xs">
          {hSaving ? "جارٍ الإضافة..." : "إضافة الإجازة"}
        </Button>
      </div>
      <div>
        {saveSettingsBtn}
      </div>
      {holidays.length > 0 && (
        <div className="space-y-1.5" data-testid="holidays-list">
          {holidays.map((h, i) => (
            <div key={h.id} data-testid={`holiday-row-${i}`}
              className="flex items-center gap-2 bg-emerald-50/50 border border-emerald-100 rounded-xl px-3 py-2 text-xs">
              <span className="font-semibold text-slate-700">{h.name}</span>
              <span className="font-mono text-slate-500" dir="ltr" data-testid={`holiday-dates-${i}`}>{h.from_date} ← {h.to_date}</span>
              <button onClick={() => deleteHoliday(h)} data-testid={`btn-delete-holiday-${i}`}
                title="حذف الإجازة" className="mr-auto text-rose-600 hover:text-rose-800 p-0.5">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const brandingCard = (
    <div className="bg-white rounded-2xl border shadow-sm p-6 space-y-5" id="branding-section" data-testid="settings-branding-section">
      <h3 className="font-bold">ترويسة التقارير والشعارات</h3>
      <p className="text-xs text-slate-400 leading-6">
        تظهر الترويسة والشعارات في أعلى كل تقرير PDF وخطابات المساءلة المطبوعة.
      </p>
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label>السطر الأول <span className="text-[10px] text-slate-400 font-normal">(ثابت من النظام)</span></Label>
          <Input value={b.line1 || "المملكة العربية السعودية"} data-testid="branding-line1" disabled readOnly
            className="bg-slate-50 text-slate-500" />
        </div>
        <div className="space-y-1.5">
          <Label>السطر الثاني <span className="text-[10px] text-slate-400 font-normal">(ثابت من النظام)</span></Label>
          <Input value={b.line2 || "وزارة التعليم"} data-testid="branding-line2" disabled readOnly
            className="bg-slate-50 text-slate-500" />
        </div>
        <div className="space-y-1.5">
          <Label>السطر الثالث (اسم المدرسة)</Label>
          <Input value={b.line3 || ""} data-testid="branding-line3"
            onChange={(e) => setB({ ...b, line3: e.target.value })} placeholder="مدرسة ..." />
        </div>
        <div className="space-y-1.5">
          <Label>اسم المدير</Label>
          <Input value={b.manager_name || ""} data-testid="branding-manager-name"
            onChange={(e) => setB({ ...b, manager_name: e.target.value })}
            placeholder="يظهر في خانة التوقيع بجميع التقارير والمساءلات المصدرة" />
        </div>
        <div className="max-w-sm">
          <LogoPicker label="توقيع المدير" value={b.signature} testId="branding-signature"
            onChange={(v) => setB({ ...b, signature: v })} />
        </div>
        <p className="text-[11px] text-slate-400 leading-5 -mt-1">
          يُضاف التوقيع تلقائياً في خانة الرئيس المباشر بخطابات المساءلة المطبوعة.
        </p>
        <div className="space-y-1.5" data-testid="branding-logo-ministry">
          <Label>شعار الوزارة <span className="text-[10px] text-slate-400 font-normal">(ثابت من النظام)</span></Label>
          <div className="border rounded-lg bg-slate-50 p-2 flex items-center gap-3">
            {b.logo_ministry && <img src={b.logo_ministry} alt="شعار الوزارة" className="h-14 w-14 object-contain" />}
            <span className="text-[11px] text-slate-400">الشعار الرسمي لوزارة التعليم — لا يمكن تغييره</span>
          </div>
        </div>
        <LogoPicker label="شعار المدرسة" value={b.logo_school} testId="branding-logo-school"
          onChange={(v) => setB({ ...b, logo_school: v })} />
      </div>

      <div className="border rounded-xl p-4 bg-slate-50" data-testid="branding-preview">
        <p className="text-[11px] text-slate-400 mb-2">معاينة الترويسة:</p>
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col items-center justify-center self-stretch text-right">
            <p className="font-bold text-sm">{b.line1 || "المملكة العربية السعودية"}</p>
            <p className="font-bold text-sm">{b.line2 || "وزارة التعليم"}</p>
            {b.line3 && <p className="font-bold text-sm">{b.line3}</p>}
            {b.manager_name && <p className="text-[11px] text-slate-500 mt-1">مدير المدرسة: {b.manager_name}</p>}
            {b.signature && (
              <div className="flex items-center gap-3 mt-1" data-testid="branding-sig-stamp-preview">
                <img src={b.signature} alt="توقيع المدير" className="h-9 object-contain" />
              </div>
            )}
          </div>
          <div className="w-28 h-28 flex items-center justify-center">
            {b.logo_ministry ? <img src={b.logo_ministry} alt="شعار الوزارة" className="max-w-full max-h-full object-contain" /> : <span className="text-[10px] text-slate-300">شعار الوزارة</span>}
          </div>
          <div className="w-24 h-24 flex items-center justify-center">
            {b.logo_school ? <img src={b.logo_school} alt="شعار المدرسة" className="max-w-full max-h-full object-contain" /> : <span className="text-[10px] text-slate-300">شعار المدرسة</span>}
          </div>
        </div>
      </div>
      <Button onClick={saveBranding} data-testid="btn-save-branding"
        className="bg-emerald-900 hover:bg-emerald-950 text-white gap-1.5">
        <Save className="w-4 h-4" /> حفظ الترويسة
      </Button>
    </div>
  );

  if (section === "all") {
    return (
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-testid="settings-tab">
        <div className="space-y-4">
          {timingCard}
          {inquiriesCard}
          {holidaysCard}
        </div>
        {brandingCard}
      </div>
    );
  }

  return (
    <div data-testid="settings-tab">
      {section === "timing" && timingCard}
      {section === "inquiries" && inquiriesCard}
      {section === "holidays" && holidaysCard}
      {section === "branding" && brandingCard}
    </div>
  );
}
